import React from "react";

/**
 * Tab strip for the template editor: "All" plus one tab per running game,
 * each showing how many of that board's yantras carry an image.
 *
 * A template is a per-yantra override, and the same yantra sits on
 * several boards, so an admin does not upload "per game" - they upload
 * per yantra and these tabs show what each game ends up looking like.
 *
 * @param {object[]} games      from /active-yantras: { id, game_name, yantra_ids }
 * @param {number}   total      active yantra count
 * @param {Set}      covered    yantra ids that currently have an image
 * @param {string}   value      "all" or a game id as string
 */
export default function BoardTabs({ games = [], total = 0, covered, value, onChange }) {
  const has = (id) => covered?.has(Number(id));
  const tabs = [
    { key: "all", label: "All", have: [...(covered || [])].length, total },
    ...games.map((g) => ({
      key: String(g.id),
      label: g.game_name,
      have: g.yantra_ids.filter(has).length,
      total: g.yantra_ids.length,
    })),
  ];

  return (
    <div className="it-board-tabs" role="tablist">
      {tabs.map((t) => {
        const complete = t.total > 0 && t.have >= t.total;
        const empty = t.have === 0;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={value === t.key}
            className={`it-board-tab${value === t.key ? " is-active" : ""}`}
            onClick={() => onChange(t.key)}
          >
            <span className="it-board-tab-label">{t.label}</span>
            <span
              className={`it-board-tab-count${
                complete ? " is-complete" : empty ? " is-empty" : " is-partial"
              }`}
            >
              {t.have}/{t.total}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Which yantras a tab shows. */
export function yantrasForTab(tabKey, yantras, games) {
  if (tabKey === "all") return yantras;
  const game = games.find((g) => String(g.id) === String(tabKey));
  if (!game) return yantras;
  const order = new Map(game.yantra_ids.map((id, i) => [Number(id), i]));
  return yantras
    .filter((y) => order.has(Number(y.id)))
    .sort((a, b) => order.get(Number(a.id)) - order.get(Number(b.id)));
}

/** Compact per-game coverage chips for the list page. */
export function CoverageChips({ games = [] }) {
  if (!games.length) return null;
  return (
    <div className="it-coverage-chips">
      {games.map((g) => {
        const complete = g.total > 0 && g.have >= g.total;
        const empty = g.have === 0;
        return (
          <span
            key={g.game_id}
            className={`it-coverage-chip${
              complete ? " is-complete" : empty ? " is-empty" : " is-partial"
            }`}
            title={`${g.game_name}: ${g.have} of ${g.total} yantras have an image`}
          >
            {g.game_name} {g.have}/{g.total}
          </span>
        );
      })}
    </div>
  );
}
